import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedComponent from '../Components/AnimatedComponent';
import DealershipActionCards from '../Components/DealershipActionCards';
import MobileHeroDiagonal from '../Components/MobileHeroDiagonal';
import NavigationBar from '../Components/NavigationBar';
import { LanguageContext } from '../Components/LanguageContext';
import handleTransformLang from '../functions/handleTransformLang';
import Layout from '../Layout';
import dealershipLandingData from '../lang/pages/dealershipLanding.json';
import NavLang from '../lang/pages/nav.json';
import Background from '../public/images/bg_main.svg';

const stepIcons = {
  activity: '/images/dealership/faaliyet-alani.svg',
  sign: '/images/dealership/sozlesmenin-imzalanmasi.svg',
  upload: '/images/dealership/belgelerin-yuklenmesi.svg',
  delivery: '/images/dealership/sozlesmenin-ulastirilmasi.svg',
  result: '/images/dealership/sonuclandirma.svg',
  cancel: '/images/dealership/basvurunun-iptali.svg',
  payment: '/images/dealership/odeme-sekli.svg',
};
const desktopStepGridColumns =
  'calc(33.333333% + 8px) calc(33.333333% + 52px) calc(33.333333% - 60px)';
const desktopStepColumnClasses = [
  'border-e border-[#b8bec8] pe-[64px]',
  'border-e border-[#b8bec8] ps-[48px] pe-[116px]',
  'ps-[26px]',
];

function getStepColumns(steps) {
  return [
    [steps[0], steps[3], steps[6]].filter(Boolean),
    [steps[1], steps[4]].filter(Boolean),
    [steps[2], steps[5]].filter(Boolean),
  ];
}

export default function DealershipLanding() {
  return (
    <Layout headData={dealershipLandingData.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);
  const [content, setContent] = useState(handleTransformLang(NavLang));
  const [pageContent, setPageContent] = useState(
    handleTransformLang(dealershipLandingData)
  );
  const [mobileOpenStepIndex, setMobileOpenStepIndex] = useState(-1);
  const stepColumns = getStepColumns(pageContent.steps || []);

  useEffect(() => {
    setContent(handleTransformLang(NavLang));
    setPageContent(handleTransformLang(dealershipLandingData));
    setMobileOpenStepIndex(-1);
  }, [language]);

  return (
    <>
      <section
        className="relative overflow-hidden bg-white pb-24 lg:min-h-[760px] lg:pb-28"
      >
        <div
          className="absolute inset-0 hidden bg-no-repeat sm:block sm:bg-[length:1180px_480px] sm:bg-top 1.5md:bg-[length:1560px_620px] 1.5md:bg-top xl:bg-[length:1760px_700px] xl:bg-top 1.5xl:bg-[length:1915px_750px] 1.5xl:bg-top"
          style={{ backgroundImage: `url(${Background})` }}
        />
        <MobileHeroDiagonal className="top-[11.25rem] h-[16.5rem] w-[12.6rem] sm:top-[5rem]" />
        <div className="h-1.5 w-full bg-bg_secondary" />

        <div className="relative z-20 container mx-auto max-w-8xl px-4 py-11">
          <NavigationBar content={content} />

          <div className="grid items-center gap-14 pt-10 lg:grid-cols-[0.84fr_1.16fr] lg:pt-16">
            <AnimatedComponent
              direction="animate-fade-right"
              className="order-2 max-w-[40rem] lg:order-1 lg:ml-[-15px] lg:pt-[216px]"
            >
              <h1 className="text-[36px] font-bold leading-[1.15] text-text_primary lg:whitespace-nowrap">
                {pageContent.hero.title}
              </h1>
              <p className="mt-6 max-w-lg text-[18px] leading-8 text-text_secondary">
                {pageContent.hero.description}
              </p>

              <div className="mt-[42px] flex gap-3 max-[369px]:flex-col max-[369px]:items-start min-[370px]:flex-nowrap">
                <Link
                  to="/dealership/application"
                  className="whitespace-nowrap rounded-full bg-gradient-to-r from-button_transition_1 via-button_transition_2 to-button_transition_1 bg-size-200 bg-pos-0 px-4 py-2 text-[16px] font-bold text-white transition-all duration-500 hover:bg-pos-100 min-[430px]:text-[18px]"
                >
                  {pageContent.hero.primaryButton}
                </Link>

                <Link
                  to="/dealership/query"
                  className="whitespace-nowrap rounded-full bg-bg_secondary px-4 py-2 text-[16px] font-bold text-white transition-colors duration-300 hover:bg-bg_quaternary min-[430px]:text-[18px]"
                >
                  {pageContent.hero.secondaryButton}
                </Link>
              </div>
            </AnimatedComponent>

            <AnimatedComponent
              direction="animate-fade-left"
              className="relative order-1 lg:order-2 lg:ps-2"
            >
              <div className="relative">
                <img
                  src="/images/dealership/hero-banner.svg"
                  alt=""
                  className="relative z-10 mx-auto mt-[1.875rem] w-full max-w-[37.4rem] object-contain sm:mt-[3.125rem] lg:mt-0 lg:translate-x-[9px] lg:translate-y-[134px]"
                />
              </div>
            </AnimatedComponent>
          </div>
        </div>
      </section>

      <section className="bg-white pb-28">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mb-14"
          >
            <h2 className="text-[37px] font-bold text-text_primary">
              {pageContent.stepsTitle}
            </h2>
          </AnimatedComponent>

          <div className="mt-[80px] md:hidden">
            {pageContent.steps.map((step, index) => {
              const iconSrc = stepIcons[step.icon];
              const isOpen = mobileOpenStepIndex === index;

              return (
                <AnimatedComponent
                  key={`${step.title}-${index}-mobile`}
                  direction="animate-fade-up animate-duration-500"
                  className="border-b border-[#ece8e1]"
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setMobileOpenStepIndex(isOpen ? -1 : index)}
                    className="flex w-full items-center gap-3 px-4 py-5 text-left"
                  >
                    <img
                      src={iconSrc}
                      alt=""
                      className="h-[3.125rem] w-auto shrink-0 object-contain"
                    />
                    <span className="flex-1 text-[17px] font-bold tracking-[0.03em] text-text_tertiary">
                      {step.title}
                    </span>
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                      className={`h-5 w-5 shrink-0 text-text_tertiary transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    >
                      <path
                        d="M5 7.5 10 12.5 15 7.5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      isOpen
                        ? 'grid-rows-[1fr] opacity-100'
                        : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="bg-bg_primary px-4 py-4">
                        <p className="text-[18px] leading-8 text-white">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedComponent>
              );
            })}
          </div>

          <div className="mt-[80px] hidden gap-x-14 gap-y-10 md:grid md:grid-cols-2 xl:hidden">
            {pageContent.steps.map((step, index) => {
              const iconSrc = stepIcons[step.icon];

              return (
                <AnimatedComponent
                  key={`${step.title}-${index}`}
                  direction="animate-fade-up animate-duration-500"
                  className="border-b border-[#b8bec8] pb-8 xl:min-h-[12.75rem]"
                >
                  <div className="mb-5 flex items-center gap-3">
                    <img
                      src={iconSrc}
                      alt=""
                      className="h-[3.125rem] w-auto shrink-0 object-contain"
                    />
                    <h3 className="text-[17px] font-bold tracking-[0.03em] text-text_tertiary">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-[18px] leading-7 text-text_secondary">
                    {step.description}
                  </p>
                </AnimatedComponent>
              );
            })}
          </div>

          <div
            className="mt-[80px] hidden xl:grid"
            style={{ gridTemplateColumns: desktopStepGridColumns }}
          >
            {stepColumns.map((column, columnIndex) => (
              <div
                key={`column-${columnIndex}`}
                className={desktopStepColumnClasses[columnIndex]}
              >
                {column.map((step, index) => {
                  const iconSrc = stepIcons[step.icon];

                  return (
                    <AnimatedComponent
                      key={`${step.title}-${index}-desktop`}
                      direction="animate-fade-up animate-duration-500"
                      className={`${
                        index < column.length - 1
                          ? 'mb-10 h-[15rem] border-b border-[#b8bec8]'
                          : ''
                      } ${index === column.length - 1 ? 'h-[14rem]' : ''}`}
                    >
                      <div className="mb-5 flex items-center gap-3">
                        <img
                          src={iconSrc}
                          alt=""
                          className="h-[3.125rem] w-auto shrink-0 object-contain"
                        />
                        <h3 className="text-[17px] font-bold tracking-[0.03em] text-text_tertiary">
                          {step.title}
                        </h3>
                      </div>
                      <p
                        className={`text-[18px] leading-7 text-text_secondary ${
                          columnIndex === 1 && index === 1
                            ? 'w-[calc(100%+72px)] max-w-none'
                            : ''
                        }`}
                      >
                        {step.description}
                      </p>
                    </AnimatedComponent>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-bg_tertiary pb-24 pt-20 lg:pb-[240px]">
        <div className="container mx-auto max-w-8xl px-4">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-text_primary">
              {pageContent.documents.title}
            </h2>
          </AnimatedComponent>

          <div className="mt-[84px] grid gap-12 lg:grid-cols-2 lg:gap-20">
            <AnimatedComponent
              direction="animate-fade-right"
              className="lg:ps-[10px]"
            >
              <h3 className="text-[17px] font-bold tracking-[0.03em] text-text_tertiary">
                {pageContent.documents.corporateTitle}
              </h3>
              <div className="mb-4 mt-2 h-px w-full bg-[#9fa7b5]" />
              <ol className="space-y-4 text-[18px] leading-8 text-text_secondary">
                {pageContent.documents.corporateItems.map((item, index) => (
                  <li key={`${item}-${index}`}>
                    {index + 1}. {item}
                  </li>
                ))}
              </ol>
            </AnimatedComponent>

            <AnimatedComponent
              direction="animate-fade-left"
              className="lg:ps-[80px]"
            >
              <h3 className="text-[17px] font-bold tracking-[0.03em] text-text_tertiary">
                {pageContent.documents.soleTitle}
              </h3>
              <div className="mb-4 mt-2 h-px w-full bg-[#9fa7b5]" />
              <ol className="space-y-4 text-[18px] leading-8 text-text_secondary">
                {pageContent.documents.soleItems.map((item, index) => (
                  <li key={`${item}-${index}`}>
                    {index + 1}. {item}
                  </li>
                ))}
              </ol>
            </AnimatedComponent>
          </div>
        </div>

        <div className="absolute inset-x-0 top-full z-10 hidden -translate-y-[30%] lg:block">
          <div className="container mx-auto max-w-8xl px-4">
            <AnimatedComponent
              direction="animate-fade-up animate-duration-500"
              className="mx-auto max-w-fit"
            >
              <DealershipActionCards
                className="grid-cols-3 gap-2"
                itemClassName="flex justify-center"
                imageClassName="w-full max-w-[18rem] object-contain transition-transform duration-300 hover:scale-105"
              />
            </AnimatedComponent>
          </div>
        </div>
      </section>

      <section className="bg-white pb-20 pt-10 lg:pt-[210px]">
        <div className="container mx-auto max-w-8xl px-4 lg:hidden">
          <AnimatedComponent
            direction="animate-fade-up animate-duration-500"
            className="mx-auto max-w-fit"
          >
            <DealershipActionCards
              className="gap-4"
              itemClassName="flex justify-center"
              imageClassName="w-full max-w-[18rem] object-contain transition-transform duration-300 hover:scale-105"
            />
          </AnimatedComponent>
        </div>
      </section>
    </>
  );
}
